import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  selectedId: null,
  draftMessage: null,
  inputKey: 0,
  list: []
};

const slice = createSlice({
  name: 'aiConversation',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.draftMessage = null;
      state.error = action.payload;
    },

    setSelectedId(state, action) {
      state.isLoading = false;
      state.selectedId = action.payload;
      state.draftMessage = null;
      state.inputKey = (state.inputKey || 0) + 1;
      // Ensure no historical messages retain the animation flag when switching conversations
      state.list.forEach((convo) => {
        if (Array.isArray(convo.messages)) {
          convo.messages.forEach((msg) => {
            msg.isLatest = false;
          });
        } else if (convo.messages && typeof convo.messages === 'object') {
          Object.values(convo.messages).forEach((group) => {
            if (Array.isArray(group)) {
              group.forEach((msg) => {
                msg.isLatest = false;
              });
            }
          });
        }
      });
    },

    setDraftMessage(state, action) {
      state.draftMessage = action.payload;
    },

    updateConversation(state, action) {
      const { selectedId, message } = action.payload;
      if (!selectedId) return;

      const convo = state.list.find((c) => String(c.id) === String(selectedId));
      if (convo) {
        if (!convo.messages || Array.isArray(convo.messages)) {
          convo.messages = convo.messages || [];
          convo.messages.push(message);
        } else {
          const keys = Object.keys(convo.messages);
          const currentKey = keys.length > 0 ? keys[keys.length - 1] : "Aujourd'hui";
          convo.messages[currentKey] = [...(convo.messages[currentKey] || []), message];
        }
      }
    },

    appendAssistantMessage(state, action) {
      const { conversationId, message } = action.payload;
      const convo = state.list.find((c) => String(c.id) === String(conversationId));
      if (convo) {
        // Clear any previous isLatest flag before appending new message
        if (Array.isArray(convo.messages)) {
          convo.messages.forEach((m) => {
            m.isLatest = false;
          });
          convo.messages.push(message);
        } else {
          convo.messages = convo.messages || {};
          Object.values(convo.messages).forEach((group) => {
            if (Array.isArray(group)) {
              group.forEach((m) => {
                m.isLatest = false;
              });
            }
          });
          const keys = Object.keys(convo.messages);
          const currentKey = keys.length > 0 ? keys[keys.length - 1] : "Aujourd'hui";
          convo.messages[currentKey] = [...(convo.messages[currentKey] || []), message];
        }
      }
      state.isLoading = false;
    },

    addNewConversation(state, action) {
      const { id, title, createdAt, userMessage, assistantMessage } = action.payload;
      const todayKey = "Aujourd'hui";
      const newConvo = {
        id,
        title,
        created_at: createdAt || todayKey,
        messages: {
          [todayKey]: [userMessage, assistantMessage].filter(Boolean)
        }
      };
      // Clear isLatest flags on older conversations
      state.list.forEach((convo) => {
        if (Array.isArray(convo.messages)) {
          convo.messages.forEach((msg) => {
            msg.isLatest = false;
          });
        } else if (convo.messages && typeof convo.messages === 'object') {
          Object.values(convo.messages).forEach((group) => {
            if (Array.isArray(group)) {
              group.forEach((msg) => {
                msg.isLatest = false;
              });
            }
          });
        }
      });
      state.list.unshift(newConvo);
      state.selectedId = id;
      state.draftMessage = null;
      state.isLoading = false;
    },

    clearLatestMessage(state, action) {
      const { messageId } = action.payload || {};
      state.list.forEach((convo) => {
        if (Array.isArray(convo.messages)) {
          convo.messages.forEach((msg) => {
            if (!messageId || String(msg.id) === String(messageId)) {
              msg.isLatest = false;
            }
          });
        } else if (convo.messages && typeof convo.messages === 'object') {
          Object.values(convo.messages).forEach((group) => {
            if (Array.isArray(group)) {
              group.forEach((msg) => {
                if (!messageId || String(msg.id) === String(messageId)) {
                  msg.isLatest = false;
                }
              });
            }
          });
        }
      });
    },

    setList(state, action) {
      state.isLoading = false;
      state.list = action.payload;
    },

    removeConversation(state, action) {
      const conversationId =
        typeof action.payload === 'object' && action.payload?.id ? action.payload.id : action.payload;
      state.list = state.list.filter((c) => String(c.id) !== String(conversationId));
      if (String(state.selectedId) === String(conversationId)) {
        state.selectedId = state.list.length > 0 ? state.list[0].id : null;
      }
      state.isLoading = false;
    },

    updateConversationTitle(state, action) {
      const { id, title } = action.payload;
      const convo = state.list.find((c) => String(c.id) === String(id));
      if (convo) {
        convo.title = title;
      }
      state.isLoading = false;
    },

    startStreaming(state, action) {
      const { conversationId, title, createdAt, userMessage } = action.payload;
      const todayKey = "Aujourd'hui";
      const existingConvo = state.list.find((c) => String(c.id) === String(conversationId));
      const streamingPlaceholder = {
        id: 'streaming-assistant',
        sender_id: 0,
        body: '',
        isStreaming: true,
        isLatest: false
      };

      if (existingConvo) {
        if (!existingConvo.messages || Array.isArray(existingConvo.messages)) {
          existingConvo.messages = existingConvo.messages || [];
          existingConvo.messages.push(streamingPlaceholder);
        } else {
          const keys = Object.keys(existingConvo.messages);
          const currentKey = keys.length > 0 ? keys[keys.length - 1] : todayKey;
          existingConvo.messages[currentKey] = [...(existingConvo.messages[currentKey] || []), streamingPlaceholder];
        }
      } else {
        const newConvo = {
          id: conversationId,
          title: title || userMessage?.body?.slice(0, 30) || 'Nouvelle conversation',
          created_at: createdAt || todayKey,
          messages: {
            [todayKey]: [userMessage, streamingPlaceholder].filter(Boolean)
          }
        };
        state.list.unshift(newConvo);
        state.selectedId = conversationId;
        state.draftMessage = null;
      }
      state.isLoading = false;
    },

    appendStreamingChunk(state, action) {
      const { conversationId, chunk } = action.payload;
      const convo = state.list.find((c) => String(c.id) === String(conversationId));
      if (!convo) return;

      const appendToMsg = (msgList) => {
        const streamingMsg = msgList.find((m) => m.isStreaming) || msgList[msgList.length - 1];
        if (streamingMsg && streamingMsg.sender_id === 0) {
          streamingMsg.body = (streamingMsg.body || '') + chunk;
        }
      };

      if (Array.isArray(convo.messages)) {
        appendToMsg(convo.messages);
      } else if (convo.messages && typeof convo.messages === 'object') {
        const keys = Object.keys(convo.messages);
        if (keys.length > 0) {
          const currentKey = keys[keys.length - 1];
          if (Array.isArray(convo.messages[currentKey])) {
            appendToMsg(convo.messages[currentKey]);
          }
        }
      }
    },

    finishStreaming(state, action) {
      const { conversationId, message, title } = action.payload;
      const convo = state.list.find((c) => String(c.id) === String(conversationId));
      if (convo) {
        if (title) {
          convo.title = title;
        }

        const finalizeMsg = (msgList) => {
          const streamingIndex = msgList.findIndex((m) => m.isStreaming || String(m.id) === 'streaming-assistant');
          if (streamingIndex !== -1) {
            msgList[streamingIndex] = {
              id: message.id || msgList[streamingIndex].id,
              sender_id: 0,
              body: message.body || msgList[streamingIndex].body,
              sources: message.sources || [],
              created_at: message.created_at || "À l'instant",
              isStreaming: false,
              isLatest: false
            };
          }
        };

        if (Array.isArray(convo.messages)) {
          finalizeMsg(convo.messages);
        } else if (convo.messages && typeof convo.messages === 'object') {
          Object.values(convo.messages).forEach((group) => {
            if (Array.isArray(group)) {
              finalizeMsg(group);
            }
          });
        }
      }
      state.isLoading = false;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  startLoading,
  setSelectedId,
  setDraftMessage,
  updateConversation,
  appendAssistantMessage,
  addNewConversation,
  clearLatestMessage,
  setList,
  removeConversation,
  updateConversationTitle,
  startStreaming,
  appendStreamingChunk,
  finishStreaming,
  hasError
} = slice.actions;

// ----------------------------------------------------------------------

export function getConversations(selectedIdToKeep = null) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/api/conversations');
      const data = response.data || [];
      dispatch(slice.actions.setList(data));

      const currentSelectedId = selectedIdToKeep || getState().aiConversation.selectedId;
      const targetId = data.some((c) => c.id === currentSelectedId) ? currentSelectedId : data[0]?.id || null;

      dispatch(slice.actions.setSelectedId(targetId));
      return data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function sendMessage(params) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    if (params.selectedId) {
      dispatch(slice.actions.updateConversation(params));
    } else {
      dispatch(slice.actions.setDraftMessage(params.message));
    }

    try {
      const authStorage = localStorage.getItem('authParams');
      const authParams = authStorage ? JSON.parse(authStorage) : {};
      const tokens = authParams.tokens || {};
      const locale = window.localStorage.getItem(`${process.env.REACT_APP_PROJECT_NAME}-i18nextLng`) || 'fr';
      const host = process.env.REACT_APP_API || 'http://localhost:3001';
      const endpoint = `${host}/${locale}/api/conversations?stream=true`;

      const headers = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'access-token': tokens.accessToken || axios.defaults.headers.common['access-token'] || '',
        'token-type': tokens.tokenType || axios.defaults.headers.common['token-type'] || '',
        client: tokens.client || axios.defaults.headers.common.client || '',
        expiry: tokens.expiry || axios.defaults.headers.common.expiry || '',
        uid: tokens.uid || axios.defaults.headers.common.uid || ''
      };
      if (axios.defaults.headers.common.Authorization) {
        headers.Authorization = axios.defaults.headers.common.Authorization;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          id: params.selectedId,
          message: params.message.body,
          stream: true
        })
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let activeConversationId = params.selectedId;

      const processStream = async () => {
        const result = await reader.read();
        if (result.done) return;

        buffer += decoder.decode(result.value, { stream: true });
        const rawEvents = buffer.split('\n\n');
        buffer = rawEvents.pop() || '';

        rawEvents.forEach((rawEvent) => {
          if (!rawEvent.trim()) return;

          let eventType = 'message';
          let dataText = '';

          const lines = rawEvent.split('\n');
          lines.forEach((line) => {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              dataText = line.slice(6).trim();
            }
          });

          if (!dataText) return;

          try {
            const data = JSON.parse(dataText);

            if (eventType === 'metadata') {
              activeConversationId = data.id;
              dispatch(
                slice.actions.startStreaming({
                  conversationId: data.id,
                  title: data.title,
                  createdAt: data.created_at,
                  userMessage: params.message
                })
              );
            } else if (eventType === 'delta') {
              dispatch(
                slice.actions.appendStreamingChunk({
                  conversationId: activeConversationId,
                  chunk: data.content
                })
              );
            } else if (eventType === 'done') {
              dispatch(
                slice.actions.finishStreaming({
                  conversationId: activeConversationId,
                  message: data,
                  title: data.conversation_title
                })
              );
            } else if (eventType === 'error') {
              dispatch(slice.actions.hasError(data.error || 'Erreur lors du streaming'));
            }
          } catch (e) {
            // Ignored malformed chunk
          }
        });

        await processStream();
      };

      await processStream();

      return { id: activeConversationId };
    } catch (error) {
      console.warn('SSE Streaming failed or not supported, falling back to standard POST', error);
      try {
        const fallbackRes = await axios.post('/api/conversations', {
          id: params.selectedId,
          message: params.message.body
        });
        const { id, title, created_at: createdAt, answer } = fallbackRes.data || {};
        if (answer) {
          const assistantMsg = { ...answer, isLatest: false };
          const existingConvo = getState().aiConversation.list.find((c) => String(c.id) === String(id));
          if (existingConvo) {
            dispatch(
              slice.actions.appendAssistantMessage({
                conversationId: id,
                message: assistantMsg
              })
            );
          } else {
            dispatch(
              slice.actions.addNewConversation({
                id,
                title: title || params.message.body.slice(0, 30),
                createdAt,
                userMessage: params.message,
                assistantMessage: assistantMsg
              })
            );
          }
        } else {
          await dispatch(getConversations(id));
        }
        return fallbackRes.data;
      } catch (fallbackError) {
        dispatch(slice.actions.hasError(fallbackError));
        throw fallbackError;
      }
    }
  };
}

export function deleteConversation(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete(`/api/conversations/${id}`);
      dispatch(slice.actions.removeConversation(id));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function renameConversation({ id, title }) {
  return async (dispatch) => {
    try {
      const response = await axios.patch(`/api/conversations/${id}`, {
        conversation: { title }
      });
      const updatedTitle = response.data?.title || title;
      dispatch(slice.actions.updateConversationTitle({ id, title: updatedTitle }));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}
